import React, { useEffect, useMemo, useState } from 'react'
import { batch, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'

import { Button, CheckBox, Dialog, Input, Typography } from '@chilecompra/react-kit/components'
import {
  checkInputErrorRequired,
  checkInputErrorMaxLength,
  checkInputErrorOnlyNumbers,
  checkInputErrorMinLength,
  checkInputErrorEmail,
  Skeleton,
  useCheckBox,
  useInput,
  useViewport
} from '@chilecompra/react-kit'

import AttachmentsWrapper from '../../../components/customs/AttachmentsWrapper.component'

import {
  onSavedDirectDealQuoteThunk,
  onCleanQuoteDirectDeal,
  onValidAttachmentsDirectDealQuoteThunk,
  onUpdateQuoteDirectDealQuote,
  onCloseDetailQuoteDirectDealQuote
} from './DirectDealQuote.actions'
import { onShowSnackbar } from '../../SnackBarProvider/SnackBarProvider.actions'

import { AmountPrefix, Grid } from './DirectDealQuote.styles'
import { Divider } from '../DirectDealStepTwo.styles'

import { formatDNI } from '../../../modules/utils/formatters'

import {
  CODE_BUSINESS_ATTACHAMENTS_DIRECT_DEAL,
  QUOTES_AREA_CODE_PHONE_DICTIONARY
} from '../../../config/settings/constants'

/**
 * The DirectDealStepTwoDetailQuote dialog
 */
const DirectDealStepTwoDetailQuoteDialog = props => {
  const { open, loading, quoteId, quotes } = props

  const dispatch = useDispatch()
  const { size } = useViewport()

  const [isEditing, setEditing] = useState(false)
  const [quoteModelEdit, setQuoteModelEdit] = useState(null)
  const [openModalAttachQuote, setOpenModalAttachQuote] = useState(false)
  const [openModalDeleteAllQuote, setOpenModalDeleteAllQuote] = useState(false)

  const {
    value: checkedForeignValue,
    onChange: handleCheckedForeign,
    onReset: onResetCheckedForeign,
    setValue: setCheckedForeign
  } = useCheckBox({
    changeCallback: updatedValue => handlerExposedAreaCode(updatedValue),
    initialValue: false
  })

  const {
    error: quoteDniError,
    onReset: onResetQuoteDni,
    value: quoteDniValue,
    setValue: setQuoteDni
  } = useInput({
    changeCallback: () => setQuoteLegalName(''),
    errorCallbacks: [checkInputErrorRequired()],
    formatCallbacks: [value => formatDNI(value)],
    initialValue: ''
  })

  const {
    error: quoteLegalNameError,
    onReset: onResetQuoteLegalName,
    value: quoteLegalNameValue,
    setValue: setQuoteLegalName
  } = useInput({
    errorCallbacks: [checkInputErrorRequired()],
    initialValue: ''
  })

  const {
    error: quotePhoneError,
    onChange: onChangeQuotePhone,
    onError: onErrorQuotePhone,
    onReset: onResetQuotePhone,
    value: quotePhoneValue,
    setValue: setQuotePhone
  } = useInput({
    errorCallbacks: [
      checkInputErrorOnlyNumbers(),
      checkInputErrorMaxLength(
        checkedForeignValue
          ? QUOTES_AREA_CODE_PHONE_DICTIONARY.FOREIGN.MAX
          : QUOTES_AREA_CODE_PHONE_DICTIONARY.NATIONAL.MAX
      ),
      checkInputErrorMinLength(
        checkedForeignValue
          ? QUOTES_AREA_CODE_PHONE_DICTIONARY.FOREIGN.MIN
          : QUOTES_AREA_CODE_PHONE_DICTIONARY.NATIONAL.MIN
      )
    ],
    initialValue: ''
  })

  const {
    error: quoteEmailError,
    onChange: onChangeQuoteEmail,
    onError: onErrorQuoteEmail,
    onReset: onResetQuoteEmail,
    value: quoteEmailValue,
    setValue: setQuoteEmail
  } = useInput({
    errorCallbacks: [checkInputErrorMaxLength(200), checkInputErrorEmail('Formato de email incorrecto.')],
    initialValue: ''
  })

  const handlerSaved = () => {
    const quoteModel = {
      ...quoteModelEdit,
      contributorDni: quoteDniValue,
      contributorLegalName: quoteLegalNameValue,
      contributorPhone: quotePhoneValue,
      contributorEmail: quoteEmailValue,
      isContributorForeign: checkedForeignValue
    }

    dispatch(
      onSavedDirectDealQuoteThunk({
        quoteModel,
        callback: quoteModel => {
          batch(() => {
            dispatch(onUpdateQuoteDirectDealQuote(quoteModel))
            dispatch(
              onShowSnackbar({
                title: 'Cotización actualizada con éxito',
                severity: 'success'
              })
            )
            handlerCloseQuote()
          })
        },
        callbackError: ({ err }) => {
          dispatch(
            onShowSnackbar({
              title: err.descripcion,
              message: 'Te pedimos que lo vuelvas a intentar dentro de unos minutos.',
              severity: 'error'
            })
          )
        }
      })
    )
  }

  const handlerCloseQuote = () => {
    if (isEditing) {
      dispatch(
        onValidAttachmentsDirectDealQuoteThunk({
          attachmentCodes: {
            business: CODE_BUSINESS_ATTACHAMENTS_DIRECT_DEAL,
            businessId: quoteModelEdit?.idBusiness,
            businessSubId: quoteModelEdit?.idSubBusiness
          },
          callback: payload => {
            if (payload.attachmentAmount > 0) {
              closerDetailDialog()
            } else {
              dispatch(
                onShowSnackbar({
                  title: 'Debes adjuntar al menos un documento para continuar',
                  severity: 'warning'
                })
              )
            }
          }
        })
      )
    } else {
      closerDetailDialog()
    }
  }

  const handlerChangeMode = () => {
    setEditing(!isEditing)
  }

  const handlerExposedAreaCode = isForeign => {
    if (isForeign) {
      setQuotePhone('')
    } else {
      setQuotePhone('56')
    }
  }

  const closerDetailDialog = () => {
    batch(() => {
      dispatch(onCloseDetailQuoteDirectDealQuote())
      dispatch(onCleanQuoteDirectDeal())
      onResetQuoteDni()
      onResetQuoteEmail()
      onResetQuoteLegalName()
      onResetQuotePhone()
      onResetCheckedForeign()
      setEditing(false)
    })
  }

  const disableSaved = useMemo(() => {
    return (
      quoteDniValue === '' ||
      quoteDniError !== '' ||
      quoteLegalNameValue === '' ||
      quoteLegalNameError !== '' ||
      (quotePhoneValue !== '' && quotePhoneError !== '') ||
      (quoteEmailValue !== '' && quoteEmailError !== '')
    )
  }, [
    quoteDniValue,
    quoteDniError,
    quoteLegalNameValue,
    quoteLegalNameError,
    quotePhoneValue,
    quotePhoneError,
    quoteEmailValue,
    quoteEmailError
  ])

  useEffect(() => {
    const quoteEdit = quotes.find(quote => quote.idQuote === quoteId)

    if (quoteEdit) {
      setQuoteModelEdit(quoteEdit)
      setQuoteDni(quoteEdit?.contributorDni)
      setQuoteLegalName(quoteEdit?.contributorLegalName)
      setQuotePhone(quoteEdit?.contributorPhone)
      setQuoteEmail(quoteEdit?.contributorEmail)
      setCheckedForeign(quoteEdit?.isContributorForeign)
    }
  }, [quoteId])

  return (
    <Dialog
      open={open}
      disableBackdropClick
      verticalAlign="top"
      maxWidth={size.isTiny || size.isSmall ? '400px' : '600px'}
      onClose={handlerCloseQuote}
    >
      <Grid container spacing={2} margin="8px 0 0">
        <Grid item margin="0 0 4px">
          {checkedForeignValue && (
            <Typography variant="h3" fontWeight="bold" color="black1" lineHeight="18px" margin="8px 0" tabIndex="0">
              Cotización Proveedor Extranjero ID {quoteModelEdit?.contributorDni || '-'}
            </Typography>
          )}
          {!checkedForeignValue && (
            <Typography variant="h3" fontWeight="bold" color="black1" lineHeight="18px" margin="8px 0" tabIndex="0">
              Cotización Rut {quoteModelEdit?.contributorDni || '-'}
            </Typography>
          )}
          <Divider margin="16px 0" />
        </Grid>
        <Grid item xs={12}>
          <CheckBox
            accessibility={{
              label: '¿El proveedor es extranjero?'
            }}
            checked={checkedForeignValue}
            disabled
            label={
              <Typography variant="body2" margin="4px 0 0 -11px" tabIndex="0" color="gray1">
                ¿El proveedor es extranjero?
              </Typography>
            }
            margin="0 0 0 -3px"
            onChange={handleCheckedForeign}
          />
        </Grid>
        <Grid item lg={6} md={6} sm={6} xs={12}>
          {checkedForeignValue && (
            <Typography variant="body2" fontWeight="bold" color="black1" tabIndex="0">
              ID Proveedor extranjero
            </Typography>
          )}
          {!checkedForeignValue && (
            <Typography variant="body2" fontWeight="bold" color="black1" tabIndex="0">
              RUT Cotizante
            </Typography>
          )}
          <Typography variant="body2" color="black1" tabIndex="0" margin="8px 0 0">
            {quoteDniValue}
          </Typography>
        </Grid>
        <Grid item lg={6} md={6} sm={6} xs={12}>
          <Typography variant="body2" fontWeight="bold" color="black1" tabIndex="0">
            Razón social cotizante
          </Typography>
          <Typography variant="body2" color="black1" tabIndex="0" margin="8px 0 0">
            {quoteLegalNameValue}
          </Typography>
        </Grid>
        <Grid item lg={6} md={6} sm={6} xs={12}>
          <Input
            disabled={!isEditing}
            error={quotePhoneError}
            label="Teléfono cotizante"
            loading={loading}
            prefix={<AmountPrefix>+</AmountPrefix>}
            onChange={onChangeQuotePhone}
            onError={onErrorQuotePhone}
            value={quotePhoneValue}
          />
        </Grid>
        <Grid item lg={6} md={6} sm={6} xs={12}>
          <Input
            disabled={!isEditing}
            error={quoteEmailError}
            label="Email cotizante"
            loading={loading}
            type="email"
            onChange={onChangeQuoteEmail}
            onError={onErrorQuoteEmail}
            value={quoteEmailValue}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1" fontWeight="bold" tabIndex="0">
            Adjuntar archivos
          </Typography>
          <Typography variant="body2" tabIndex="0">
            Se admiten archivos en formato con un peso máximo de 20mb.
          </Typography>

          {!loading && quoteModelEdit && (
            <AttachmentsWrapper
              business={CODE_BUSINESS_ATTACHAMENTS_DIRECT_DEAL}
              idBusiness={quoteModelEdit?.idBusiness}
              subBusiness={quoteModelEdit?.idSubBusiness}
              isPrivate
              color="blank"
              canRead
              canDelete={isEditing}
              canWrite={isEditing}
              canWriteBulk={isEditing}
              openModal={openModalAttachQuote}
              openModalDeleteAll={openModalDeleteAllQuote}
              setOpenModal={setOpenModalAttachQuote}
              setOpenModalDeleteAll={setOpenModalDeleteAllQuote}
            />
          )}

          {loading && <Skeleton width="250px" />}
        </Grid>
        <Grid item lg={6} md={6} sm={6} xs={12} textAlign="left" order={{ lg: 1, md: 1, sm: 1, xs: 2 }}>
          <Button aria-label="Volver" width={size.isSmall || size.isTiny ? '100%' : '70%'} onClick={handlerCloseQuote}>
            Volver
          </Button>
        </Grid>
        {!isEditing && (
          <Grid item lg={6} md={6} sm={6} xs={12} textAlign="left" order={{ lg: 2, md: 2, sm: 2, xs: 1 }}>
            <Button
              aria-label="Editar"
              color="primary"
              disabled={disableSaved}
              variant="outlined"
              width={size.isSmall || size.isTiny ? '100%' : '70%'}
              onClick={handlerChangeMode}
            >
              Editar
            </Button>
          </Grid>
        )}
        {isEditing && (
          <Grid item lg={6} md={6} sm={6} xs={12} textAlign="left" order={{ lg: 2, md: 2, sm: 2, xs: 1 }}>
            <Button
              aria-label="Guardar cotización"
              color="primary"
              disabled={disableSaved}
              loading={loading}
              width={size.isSmall || size.isTiny ? '100%' : '80%'}
              onClick={handlerSaved}
            >
              Guardar cotización
            </Button>
          </Grid>
        )}
      </Grid>
    </Dialog>
  )
}

DirectDealStepTwoDetailQuoteDialog.propTypes = {
  loading: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  quoteId: PropTypes.string,
  quotes: PropTypes.arrayOf(
    PropTypes.shape({
      idQuote: PropTypes.string,
      contributorDni: PropTypes.string,
      contributorLegalName: PropTypes.string,
      contributorPhone: PropTypes.string,
      contributorEmail: PropTypes.string
    })
  )
}

export default DirectDealStepTwoDetailQuoteDialog
